import React, { useState, useEffect } from 'react';
import RentalBooking from "./rentalBooking";
import Skeleton from '../ui/Skeleton';
import { Link } from 'react-router-dom';

const renderSkeleton = () => (
    <div className="flex flex-col justify-between items-center gap-3 p-4 md:p-5 min-h-[220px] md:min-h-[280px]">
        <Skeleton className="h-32 md:h-40 lg:h-44 w-full rounded-2xl" />
        <div className="w-full">
            <Skeleton className="h-4 w-20 md:w-24 mx-auto rounded-md mb-2" />
            <Skeleton className="h-1 w-12 mx-auto rounded-full" />
        </div>
    </div>
);

const SingleBox = () => {
    const [typeData, setTypeData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchType = async () => {
            try {
                const res = await fetch('/api/listing/type');
                if (!res.ok) {
                    console.error('API error:', res.status);
                    setTypeData([]);
                    setLoading(false);
                    return;
                }
                let typeData = await res.json();
                // API now returns objects with type and image
                if (Array.isArray(typeData)) {
                    typeData = typeData.map(item => ({
                        type: item.type || item,
                        image: item.image || null
                    }));
                    typeData = typeData.sort(() => Math.random() - 0.5);
                } else {
                    typeData = [];
                }
                setTypeData(typeData);
                setLoading(false);
            } catch (e) {
                setTypeData([]);
                setLoading(false);
            }
        };
        fetchType();
    }, []);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Vehicle Types Section */}
            <div className="w-full order-2 lg:order-1">
                <div className='text-start mb-10'>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-1.5 w-16 bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] rounded-full shadow-md shadow-[#DC2626]/30"></div>
                        <span className="text-sm font-bold text-[#DC2626] uppercase tracking-wider">Vehicle Categories</span>
                    </div>
                    <h1 className='font-display text-4xl md:text-5xl font-bold mb-5 text-gray-900 leading-tight'>
                        Explore Vehicle <span className="text-[#DC2626]">Types</span>
                    </h1>
                    <p className='text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl'>
                        Browse through different vehicle categories and find the one that suits your needs perfectly. Discover your ideal ride today.
                    </p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
                        {Array(6).fill(null).map((_, index) => (
                            <div key={index} className="bg-white rounded-2xl border-2 border-gray-200">
                                {renderSkeleton()}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
                        {typeData.slice(0, 6).map((item, index) => {
                            const typeName = typeof item === 'string' ? item : (item.type || item);
                            const typeImage = typeof item === 'object' ? item.image : null;
                            return (
                                <Link 
                                    to={`/cars/type/${typeName}`}
                                    key={`${typeName}-${index}`}
                                className='group relative flex flex-col justify-between items-center gap-3 p-4 md:p-5 rounded-2xl bg-gradient-to-br from-white to-gray-50 hover:from-white hover:to-red-50/80 border-2 border-gray-200 hover:border-[#DC2626] transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 shadow-md hover:shadow-2xl hover:shadow-[#DC2626]/20 overflow-hidden min-h-[220px] md:min-h-[280px]'
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626]/0 via-[#DC2626]/0 to-[#DC2626]/0 group-hover:from-[#DC2626]/5 group-hover:via-[#DC2626]/10 group-hover:to-[#DC2626]/5 transition-all duration-500"></div>
                                
                                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-100/0 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:from-red-100/60"></div>
                                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-red-50/0 to-transparent rounded-tr-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:from-red-50/40"></div>
                                
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#DC2626]/0 to-transparent opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>
                                
                                <div className="relative w-full h-32 md:h-40 lg:h-44 flex-shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-100 via-gray-50 to-white shadow-lg group-hover:shadow-2xl group-hover:shadow-[#DC2626]/40 transition-all duration-500 border-2 border-gray-200 group-hover:border-[#DC2626]">
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#DC2626]/0 to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                                    
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                                    
                                    <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/30 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                                    
                                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out bg-gradient-to-r from-transparent via-white/40 to-transparent z-20"></div>
                                    
                                    <div className="absolute inset-0 bg-radial-gradient from-transparent via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" 
                                         style={{background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.1) 100%)'}}></div>
                                    
                                    {typeImage ? (
                                        <img 
                                            src={typeImage} 
                                            alt={typeName} 
                                            className='relative w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-125 group-hover:brightness-110 group-hover:contrast-110 group-hover:saturate-110 filter' 
                                            loading="lazy"
                                            decoding="async"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = `<div class="w-full h-full rounded-2xl bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 flex items-center justify-center text-gray-500 font-semibold text-sm">${typeName}</div>`;
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full rounded-2xl bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 flex items-center justify-center text-gray-500 font-semibold text-sm">
                                            {typeName}
                                        </div>
                                    )}
                                    
                                    <div className="absolute top-3 right-3 w-3 h-3 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 shadow-xl shadow-[#DC2626]/60 z-20 animate-pulse"></div>
                                    <div className="absolute top-3 right-3 w-3 h-3 bg-[#DC2626] rounded-full opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-700 z-10"></div>
                                    
                                    <div className="absolute bottom-3 left-3 w-2 h-8 bg-gradient-to-t from-[#DC2626] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full z-20"></div>
                                    
                                    <div className="absolute inset-0 rounded-2xl border-2 border-[#DC2626]/0 group-hover:border-[#DC2626]/50 transition-all duration-500 z-10 pointer-events-none"></div>
                                </div>
                                
                                <div className="text-center relative z-10 w-full flex-shrink-0">
                                    <h2 className='font-display text-sm md:text-base font-bold text-gray-900 group-hover:text-[#DC2626] transition-all duration-300 mb-1.5 line-clamp-2'>
                                        {typeName}
                                    </h2>
                                    <div className="mt-1.5 h-0.5 w-0 bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] mx-auto group-hover:w-10 transition-all duration-500 rounded-full shadow-md shadow-[#DC2626]/50"></div>
                                    
                                    <div className="flex gap-1 justify-center mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        <div className="w-1 h-1 bg-[#DC2626] rounded-full"></div>
                                        <div className="w-1 h-1 bg-[#DC2626] rounded-full"></div>
                                        <div className="w-1 h-1 bg-[#DC2626] rounded-full"></div>
                                    </div>
                                </div>
                                
                                <div className="absolute inset-0 rounded-2xl border-2 border-[#DC2626] opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none"></div>
                            </Link>
                            );
                        })}
                    </div>
                )}
            </div>
            
            {/* Rental Booking Section */}
            <div className='w-full order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start'>
                <RentalBooking />
            </div>
        </div>
    );
};

export default SingleBox;
